import React from 'react';
import { Box, Typography } from '@mui/material';
import {
    BulletList, Code, Field, Prose, Section, SourceRefs,
} from './DetailPrimitives';
import OntologyMeshSection from './OntologyMeshSection';
import DetectedByScenariosSection from './DetectedByScenariosSection';
import { dsVar, ink } from '../../theme/tokens';

/**
 * A concept, read the way an investigator reads one.
 *
 * `definition` is deliberately terse — one clause, inlined verbatim into a one-line
 * rationale on the case queue — so the depth lives in `guidance` and that is what gets
 * the room here. `commonFalsePositives` is given prominence because it is the field
 * investigators use most: every signal fires on lawful activity somewhere, and naming
 * the innocent explanation is what turns a flag into an investigation.
 *
 * DEGRADES GRACEFULLY BY DESIGN. Only 19 of 262 concepts currently carry guidance, so
 * most records will show a definition, maybe a measurement block, and nothing else. Every
 * section returns null when empty rather than rendering an empty heading.
 */
const ConceptDetailBody: React.FC<{ document: Record<string, any> }> = ({ document }) => {
    const guidance = document.guidance || {};
    const measurement = document.measurement || {};
    const tuning = document.tuning || {};

    const hasGuidance = Object.values(guidance).some(
        (v) => v && (!Array.isArray(v) || v.length > 0),
    );

    return (
        <Box>
            {document.definition && (
                <Typography
                    component="p"
                    sx={{ fontSize: '1rem', color: dsVar('fg-body'), lineHeight: 1.7, m: 0 }}
                >
                    {document.definition}
                </Typography>
            )}

            {!document.definition && !hasGuidance && (
                <Typography sx={{ fontSize: '0.875rem', color: dsVar('fg-muted'), fontStyle: 'italic' }}>
                    No definition recorded for this concept yet.
                </Typography>
            )}

            {hasGuidance && (
                <>
                    <Section title="What it means">
                        <Prose>{guidance.what_it_means}</Prose>
                        <Prose>{guidance.why_it_matters}</Prose>
                    </Section>

                    {/* Ahead of red flags on purpose: an analyst who has already seen the
                        flag needs the innocent explanation more than another indicator. */}
                    <Section title="Common false positives">
                        <BulletList items={guidance.common_false_positives} />
                    </Section>

                    <Section title="Red flag indicators">
                        <BulletList items={guidance.red_flag_indicators} />
                    </Section>

                    <Section title="Investigative steps">
                        <BulletList items={guidance.investigative_steps} ordered />
                    </Section>

                    <Section title="Evidence to collect">
                        <BulletList items={guidance.evidence_to_collect} />
                    </Section>

                    {(guidance.sar_narrative_guidance || guidance.escalation_guidance) && (
                        <Section title="Reporting and escalation">
                            <Prose>{guidance.sar_narrative_guidance}</Prose>
                            <Prose>{guidance.escalation_guidance}</Prose>
                        </Section>
                    )}
                </>
            )}

            {/* Where the value comes from. `direction` is the part a reader cannot infer:
                a z-score can be riskier in EITHER direction, and reading it on a one-sided
                scale is simply wrong. */}
            {(measurement.feature_path || measurement.direction
                || (measurement.computed_by?.length ?? 0) > 0) && (
                <Section title="How it is measured">
                    <Field label="Feature path" value={measurement.feature_path && <Code>{measurement.feature_path}</Code>} />
                    <Field label="Profile column" value={measurement.profile_column && <Code>{measurement.profile_column}</Code>} />
                    <Field label="Direction of risk" value={measurement.direction} />
                    <Field
                        label="Value range"
                        value={measurement.value_range && (
                            Array.isArray(measurement.value_range)
                                ? measurement.value_range.join(' to ')
                                : String(measurement.value_range)
                        )}
                    />
                    {(measurement.computed_by?.length ?? 0) > 0 && (
                        <Field
                            label="Computed by"
                            value={<Code>{measurement.computed_by.join(', ')}</Code>}
                        />
                    )}
                    {(measurement.surfaced_by?.length ?? 0) > 0 && (
                        <Field
                            label="Shown to investigators by"
                            value={<Code>{measurement.surfaced_by.join(', ')}</Code>}
                        />
                    )}
                </Section>
            )}

            {/* A control's safe operating range. The regulatory floor is the part that
                turns "this looks low" into a finding. */}
            {(tuning.default_value !== undefined || tuning.sensitivity_note || tuning.regulatory_floor) && (
                <Section title="Tuning">
                    <Field
                        label="Platform default"
                        value={tuning.default_value !== undefined && tuning.default_value !== null
                            ? String(tuning.default_value) : null}
                    />
                    <Field
                        label="Recommended range"
                        value={tuning.recommended_range && (
                            Array.isArray(tuning.recommended_range)
                                ? tuning.recommended_range.join(' to ')
                                : String(tuning.recommended_range)
                        )}
                    />
                    <Field label="Sensitivity" value={tuning.sensitivity_note} />
                    {tuning.regulatory_floor && (
                        <Box
                            sx={{
                                mt: 1, p: 1.25, borderRadius: '6px',
                                backgroundColor: 'rgba(125, 211, 252, 0.08)',
                                border: '1px solid rgba(125, 211, 252, 0.3)',
                            }}
                        >
                            <Typography sx={{ fontSize: '0.75rem', color: ink('#7DD3FC'), mb: 0.25 }}>
                                Regulatory floor
                            </Typography>
                            <Typography sx={{ fontSize: '0.8125rem', color: ink('#BAE6FD') }}>
                                {tuning.regulatory_floor}
                            </Typography>
                        </Box>
                    )}
                </Section>
            )}

            {Array.isArray(document.synonyms) && document.synonyms.length > 0 && (
                <Section title="Also known as">
                    <Typography sx={{ fontSize: '0.875rem', color: dsVar('fg-soft') }}>
                        {document.synonyms.join(', ')}
                    </Typography>
                </Section>
            )}

            {document.concept_type === 'typology' && document.concept_code && (
                <DetectedByScenariosSection typologyCode={document.concept_code} />
            )}

            <OntologyMeshSection kind="concepts" code={document.concept_code} />

            <Section title="Sources">
                <SourceRefs refs={document.source_refs} />
            </Section>
        </Box>
    );
};

export default ConceptDetailBody;
